import { useState, useCallback, useEffect } from 'react';
import { FeeComponentModel, PerformanceFeeStructure } from '../../../types/client';
import { clientMasterApi } from '../../../services/api';
import Swal from 'sweetalert2';
import { confirmColors } from '@/components/ui';
import { toast } from 'react-toastify';

interface UseFeeStructureProps {
    clientId?: number;
    clientCode?: string;
    schemeId?: number;
    viewMode?: boolean;
    onModified?: () => void;
    data?: FeeComponentModel[];
    previousFeeComponents?: FeeComponentModel[];
}

interface FeeStructureChoices {
    hurdle_benchmark_choices: Array<{ value: string; label: string }>;
    hurdle_type_choices: Array<{ value: string; label: string }>;
    duration_choices: Array<{ value: string; label: string }>;
}

// Fee-structure metadata is static dropdown choices — fetch it once per
// session and share across all hook instances (also dedupes StrictMode's
// double-mounted effects). The singleton promise means concurrent callers
// await the same in-flight request.
// Call resetFeeStructureMetadataCache() in test afterEach to prevent stale
// state leaking across test runs (Jest modules are cached between tests).
let metadataCache: FeeStructureChoices | null = null;
let metadataPromise: Promise<FeeStructureChoices> | null = null;

export function resetFeeStructureMetadataCache(): void {
    metadataCache = null;
    metadataPromise = null;
}

const getFeeStructureMetadataCached = (): Promise<FeeStructureChoices> => {
    if (metadataCache) return Promise.resolve(metadataCache);
    if (!metadataPromise) {
        metadataPromise = clientMasterApi.getFeeStructureMetadata()
            .then((meta: FeeStructureChoices) => {
                metadataCache = meta;
                return meta;
            })
            .catch((err: unknown) => {
                metadataPromise = null; // allow retry on the next fetch
                throw err;
            });
    }
    return metadataPromise;
};

export const useFeeStructure = ({
    clientId,
    clientCode,
    schemeId,
    viewMode,
    onModified,
    data,
    previousFeeComponents
}: UseFeeStructureProps) => {
    const [mode, setMode] = useState<'list' | 'form'>('list');
    const [feeComponentsList, setFeeComponentsList] = useState<FeeComponentModel[]>([]);
    // Starts true when the mount effect will load the list, so "No fee structures" never flashes first.
    const [listLoading, setListLoading] = useState(() => !!clientId || !!data);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState<number | undefined>();

    const [feeComponent, setFeeComponent] = useState<FeeComponentModel | null>(null);
    const [validationErrors, setValidationErrors] = useState<any>({ fee_structures: [] });

    const [error, setError] = useState<string | null>(null);
    const [isViewingHistory, setIsViewingHistory] = useState(false);
    const [deletedStructures, setDeletedStructures] = useState<number[]>([]);
    const [choices, setChoices] = useState<FeeStructureChoices>({
        hurdle_benchmark_choices: [],
        hurdle_type_choices: [],
        duration_choices: []
    });

    const fetchFeeComponents = useCallback(async (signal?: AbortSignal) => {
        // Kick off the (cached) dropdown-choices fetch; it resolves in
        // parallel with the fee-components request instead of serializing.
        const metaPromise = getFeeStructureMetadataCached().catch((err: unknown) => {
            console.error('Failed to fetch fee structure metadata:', err);
            return null;
        });

        if (data) {
            const metaResponse = await metaPromise;
            // metaPromise has no abort support (module-level singleton with no signal),
            // so guard state updates against a component that unmounted while it was in flight.
            if (metaResponse && !signal?.aborted) {
                setChoices(metaResponse);
            }
            setFeeComponentsList(data);
            setListLoading(false);

            // Extract display names from provided data if available
            if (data.length > 0) {
                const first = data[0];
                const structureWithChoices = first.fee_structures?.find((s: any) => s.hurdle_type_choices?.length > 0);
                if (structureWithChoices || first.duration_choices) {
                    setChoices(prev => ({
                        ...prev,
                        duration_choices: first.duration_choices || prev.duration_choices,
                        hurdle_type_choices: structureWithChoices?.hurdle_type_choices || prev.hurdle_type_choices,
                        hurdle_benchmark_choices: structureWithChoices?.hurdle_benchmark_choices || prev.hurdle_benchmark_choices,
                    }));
                }
            }
            return;
        }

        // New client: no list to load, but the form still needs the dropdown choices.
        if (!clientId) {
            setListLoading(false);
            const metaResponse = await metaPromise;
            if (metaResponse && !signal?.aborted) {
                setChoices(metaResponse);
            }
            return;
        }

        try {
            setListLoading(true);
            const [metaResponse, response] = await Promise.all([
                metaPromise,
                clientMasterApi.getFeeComponents(clientId, { signal }),
            ]);
            // Apply metadata first so list-derived choices (below) can override it.
            if (metaResponse && !signal?.aborted) {
                setChoices(metaResponse);
            }

            let list = [];
            if (response && (response as any).results) {
                list = (response as any).results;
                if ((response as any).meta) {
                    setChoices(prev => ({ ...prev, ...(response as any).meta }));
                }
            } else {
                list = Array.isArray(response) ? response : ([response]);
            }

            setFeeComponentsList(list);

            // If metadata wasn't in list.meta, it might be in the items themselves
            if (list.length > 0) {
                const first = list[0];
                const structureWithChoices = first.fee_structures?.find((s: any) => s.hurdle_type_choices?.length > 0);

                setChoices(prev => {
                    // Only update if the values are truthy to avoid clearing API-level metadata
                    const updates: any = {};
                    if (first.duration_choices?.length) updates.duration_choices = first.duration_choices;
                    if (structureWithChoices?.hurdle_type_choices?.length) updates.hurdle_type_choices = structureWithChoices.hurdle_type_choices;
                    if (structureWithChoices?.hurdle_benchmark_choices?.length) updates.hurdle_benchmark_choices = structureWithChoices.hurdle_benchmark_choices;

                    return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
                });
            }
        } catch (err: any) {
            if (err?.response && err.response.status === 404) {
                setFeeComponentsList([]);
                        } else if (err && err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
                            console.error('Error fetching fee components metadata:', err);
                            // Don't set global error for new clients failing to fetch meta, just use defaults
                            if (clientId) setError('Failed to fetch fee structures history.');
                        }        } finally {
            if (!signal?.aborted) {
                setListLoading(false);
            }
        }
    }, [clientId, data]);

    useEffect(() => {
        const controller = new AbortController();
        fetchFeeComponents(controller.signal);
        return () => controller.abort();
    }, [fetchFeeComponents]);

    const handleCreateNew = () => {
        const latest = feeComponentsList.length > 0 ? feeComponentsList[0] : null;
        const newComponent: FeeComponentModel = latest
            ? {
                ...latest,
                id: undefined,
                supersedes: latest.id,
                valid_from: new Date().toISOString().split('T')[0],
                valid_to: null,
                is_active: true,
                fee_structures: latest.fee_structures.map(fs => ({ ...fs }))
            }
            : {
                client_code: clientCode || '',
                scheme: schemeId || 0,
                management_fee: 0,
                management_fee_calc_duration: 'yearly',
                performance_fee_calc_duration: 'yearly',
                distributor_manage_fee_share: 0,
                distributor_performance_fee_share: 0,
                fee_structures: [],
                valid_from: new Date().toISOString().split('T')[0],
                is_active: true
            };

        setFeeComponent(newComponent);
        setValidationErrors({ fee_structures: new Array(newComponent.fee_structures.length).fill({}) });
        setIsViewingHistory(false);
        setDeletedStructures([]);
        setMode('form');
        if (onModified) onModified();
    };

    const handleViewDetails = (component: FeeComponentModel) => {
        setFeeComponent(component);
        setValidationErrors({ fee_structures: new Array(component.fee_structures.length).fill({}) });
        setIsViewingHistory(true);
        setDeletedStructures([]);
        setMode('form');
    };

    const handleEdit = (component: FeeComponentModel) => {
        setFeeComponent({ ...component, supersedes: component.id });
        setValidationErrors({ fee_structures: new Array(component.fee_structures.length).fill({}) });
        setIsViewingHistory(false);
        setDeletedStructures([]);
        setMode('form');
    };

    const handleDelete = async (component: FeeComponentModel) => {
        if (viewMode || !component.id) return;
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'You are about to mark this fee component inactive.',
            icon: 'warning',
            showCancelButton: true,
            ...confirmColors('danger'),
            confirmButtonText: 'Yes, mark inactive!'
        });

        if (!result.isConfirmed) return;

        try {
            setActionLoadingId(component.id);
            if (clientId && component.id) {
                await clientMasterApi.deleteFeeComponent(clientId, component.id);
            }
            if (onModified) onModified();
            await fetchFeeComponents();
            Swal.fire('Deleted!', 'The fee component has been deleted.', 'success');
        } catch (err) {
            console.error('Failed to delete fee component', err);
            setError('Failed to delete fee component');
            Swal.fire('Error!', 'Failed to delete fee component.', 'error');
        } finally {
            setActionLoadingId(undefined);
        }
    };

    const handleBackToList = () => {
        setMode('list');
        setFeeComponent(null);
        setIsViewingHistory(false);
    };

    const handleInputChange = (field: keyof FeeComponentModel, value: any) => {
        if (!feeComponent || isViewingHistory || viewMode) return;
        setFeeComponent({ ...feeComponent, [field]: value });

        if (validationErrors[field as string]) {
            setValidationErrors((prev: any) => {
                const updated = { ...prev };
                delete updated[field as string];
                return updated;
            });
        }

        if (onModified) onModified();
    };

    const handleStructureChange = (index: number, field: keyof PerformanceFeeStructure, value: any) => {
        if (!feeComponent || isViewingHistory || viewMode) return;
        const newStructures = [...feeComponent.fee_structures];
        const structure: Partial<PerformanceFeeStructure> = { ...(newStructures[index] as PerformanceFeeStructure) };

        (structure as any)[field] = value;
        if (field === 'hurdle_type') {
            if (value === 'Benchmark') {
                structure.hurdle_value = undefined;
                if (!structure.hurdle_benchmark) structure.hurdle_benchmark = undefined;
            } else if (value === 'Value') {
                structure.hurdle_benchmark = undefined;
                if (structure.hurdle_value === undefined || structure.hurdle_value === null) structure.hurdle_value = undefined;
            }
        }

        newStructures[index] = structure as PerformanceFeeStructure;
        setFeeComponent({ ...feeComponent, fee_structures: newStructures });

        if (validationErrors.fee_structures?.[index]?.[field]) {
            setValidationErrors((prev: any) => {
                const next = { ...prev };
                const nextStructures = [...(next.fee_structures || [])];
                if (nextStructures[index]) {
                    nextStructures[index] = { ...nextStructures[index] };
                    delete nextStructures[index][field];
                }
                next.fee_structures = nextStructures;
                if (nextStructures.length > 0) {
                    delete next.fee_structures_general;
                }
                return next;
            });
        }

        if (onModified) onModified();
    };

    const addStructure = () => {
        if (!feeComponent || isViewingHistory || viewMode) return;
        const newStructure: PerformanceFeeStructure = {
            id: Date.now(),
            step_no: feeComponent.fee_structures.length + 1,
            hurdle_type: 'Value',
            fee_value: '',
            hurdle_value: '',
            fee_component: feeComponent.id ? feeComponent.id : 0,
        };
        setFeeComponent({
            ...feeComponent,
            fee_structures: [...feeComponent.fee_structures, newStructure],
        });
        setValidationErrors((prev: any) => ({
            ...prev,
            fee_structures: [...(prev?.fee_structures || []), {}]
        }));
        if (onModified) onModified();
    };

    const removeStructure = (index: number) => {
        if (!feeComponent || isViewingHistory || viewMode) return;
        const structureToRemove = feeComponent.fee_structures[index];

        if (structureToRemove.id && structureToRemove.id < 1000000000000) {
            setDeletedStructures(prev => [...prev, structureToRemove.id]);
        }

        const newStructures = feeComponent.fee_structures.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_no: i + 1 }));
        setFeeComponent({ ...feeComponent, fee_structures: newStructures });
        setValidationErrors((prev: any) => {
            const arr = [...(prev?.fee_structures || [])];
            if (arr.length > index) {
                arr.splice(index, 1);
            }
            return { ...prev, fee_structures: arr };
        });
        if (onModified) onModified();
    };

    const validate = () => {
        const errors: any = { fee_structures: [] as any[] };
        let isValid = true;
        if (!feeComponent) return false;

        // --- Date Validation ---
        if (!feeComponent.valid_from) {
            errors.valid_from = 'Valid From date is required';
            isValid = false;
        } else {
            const currentStart = new Date(feeComponent.valid_from).getTime();
            const currentEnd = feeComponent.valid_to ? new Date(feeComponent.valid_to).getTime() : Infinity;

            if (feeComponent.valid_to && currentStart >= currentEnd) {
                errors.valid_to = 'Valid To must be after Valid From';
                isValid = false;
            }

            // Check for overlaps with other components
            const isOverlapping = feeComponentsList.some(existing => {
                // Skip self (if editing)
                if (existing.id === feeComponent.id) return false;
                if (!existing.valid_from) return false;

                const existingStart = new Date(existing.valid_from).getTime();
                // The backend closes the superseded range at the new valid_from, so only a later start is valid against it.
                if (existing.id === feeComponent.supersedes && currentStart > existingStart) return false;
                const existingEnd = existing.valid_to ? new Date(existing.valid_to).getTime() : Infinity;

                // Overlap condition: StartA < EndB && EndA > StartB
                return currentStart < existingEnd && currentEnd > existingStart;
            });

            if (isOverlapping) {
                errors.valid_from = 'Date range overlaps with an existing fee component.';
                isValid = false;
            }

            // Check for duplicate valid_from specifically (cleaner error message)
            const isDuplicateStart = feeComponentsList.some(existing =>
                existing.id !== feeComponent.id && existing.valid_from === feeComponent.valid_from
            );
            if (isDuplicateStart) {
                errors.valid_from = 'A fee component with this start date already exists.';
                isValid = false;
            }
        }

        if (feeComponent.management_fee === undefined || feeComponent.management_fee === null || (feeComponent.management_fee as any) === '') {
            errors.management_fee = 'Management Fee is required';
            isValid = false;
        } else if (Number(feeComponent.management_fee) < 0) {
            errors.management_fee = 'Must be non-negative';
            isValid = false;
        }

        if (feeComponent.distributor_manage_fee_share !== undefined && feeComponent.distributor_manage_fee_share !== null && (feeComponent.distributor_manage_fee_share as any) !== '' && Number(feeComponent.distributor_manage_fee_share) < 0) {
            errors.distributor_manage_fee_share = 'Must be non-negative';
            isValid = false;
        }

        if (feeComponent.distributor_performance_fee_share !== undefined && feeComponent.distributor_performance_fee_share !== null && (feeComponent.distributor_performance_fee_share as any) !== '' && Number(feeComponent.distributor_performance_fee_share) < 0) {
            errors.distributor_performance_fee_share = 'Must be non-negative';
            isValid = false;
        }

        if (Array.isArray(feeComponent.fee_structures)) {
            feeComponent.fee_structures.forEach((s, index) => {
                const sErrors: any = {};
                if (!s.step_no) {
                    sErrors.step_no = 'Required';
                    isValid = false;
                }
                if (!s.hurdle_type) {
                    sErrors.hurdle_type = 'Required';
                    isValid = false;
                }
                if (s.hurdle_type === 'Benchmark' && !s.hurdle_benchmark) {
                    sErrors.hurdle_benchmark = 'Required for Benchmark type';
                    isValid = false;
                }
                if (s.hurdle_type === 'Value' && (s.hurdle_value === undefined || s.hurdle_value === null || s.hurdle_value === '')) {
                    sErrors.hurdle_value = 'Required for Value type';
                    isValid = false;
                }
                if (s.fee_value === undefined || s.fee_value === null || s.fee_value === '') {
                    sErrors.fee_value = 'Required';
                    isValid = false;
                } else if (Number(s.fee_value) < 0) {
                    sErrors.fee_value = 'Must be non-negative';
                    isValid = false;
                }
                errors.fee_structures[index] = sErrors;
            });
        }

        setValidationErrors(errors);
        return isValid;
    };

    const handleSave = useCallback(async () => {
        if (!validate()) {
            toast.error('Please check the form for errors.');
            return;
        }

        try {
            setLoading(true);

            if (feeComponent && feeComponent.id && clientId) {
                // Updating existing fee component - can send everything together
                await clientMasterApi.updateFeeComponent(clientId, feeComponent);
            } else if (clientId && feeComponent) {
                // Creating new fee component - need to save in two steps
                // Step 1: Save fee component WITHOUT fee_structures
                const { fee_structures, ...feeComponentData } = feeComponent;
                const savedFeeComponent = await clientMasterApi.addFeeComponent(clientId, {
                    ...feeComponentData,
                    fee_structures: [] // Send empty array first
                });

                // Step 2: If we have fee_structures, update the fee component with them
                if (fee_structures && fee_structures.length > 0) {
                    const structuresWithCorrectId = fee_structures.map(fs => ({
                        ...fs,
                        id: undefined, // Remove temp IDs
                        fee_component: savedFeeComponent.id // Use the actual fee component ID
                    }));

                    await clientMasterApi.updateFeeComponent(clientId, {
                        ...savedFeeComponent,
                        fee_structures: structuresWithCorrectId
                    });
                }
            }

            toast.success('Fee component saved successfully');
            setMode('list');
            fetchFeeComponents();
            setFeeComponent(null);
            setIsViewingHistory(false);
        } catch (err: any) {
            console.error('Error saving fee component:', err);

            if (err.response && err.response.data) {
                const backendErrors = err.response.data;
                const newValidationErrors: any = { fee_structures: [] };
                const nonFieldErrors: string[] = [];

                Object.keys(backendErrors).forEach(key => {
                    const errorValue = backendErrors[key];
                    const errorMessage = Array.isArray(errorValue) ? errorValue.join(' ') : String(errorValue);

                    if (key === 'non_field_errors' || key === 'detail') {
                        nonFieldErrors.push(errorMessage);
                    } else if (key === 'fee_structures' && Array.isArray(errorValue)) {
                        // Handle nested errors for fee_structures
                        newValidationErrors.fee_structures = errorValue.map((itemError: any) => {
                            if (!itemError) return {};
                            const itemErrors: any = {};
                            Object.keys(itemError).forEach(k => {
                                const v = itemError[k];
                                itemErrors[k] = Array.isArray(v) ? v.join(' ') : String(v);
                            });
                            return itemErrors;
                        });
                    } else {
                        newValidationErrors[key] = errorMessage;
                    }
                });

                if (Object.keys(newValidationErrors).length > 1 || newValidationErrors.fee_structures.length > 0) {
                    setValidationErrors((prev: any) => ({
                        ...prev,
                        ...newValidationErrors
                    }));
                    toast.error('Please check the form for errors.');
                }

                if (nonFieldErrors.length > 0) {
                    nonFieldErrors.forEach(msg => toast.error(msg));
                } else if (Object.keys(newValidationErrors).length <= 1 && newValidationErrors.fee_structures.length === 0) {
                    toast.error('Failed to save fee component: ' + (err.message || 'Unknown error'));
                }

            } else {
                toast.error('Failed to save fee component');
            }
        } finally {
            setLoading(false);
        }
    }, [feeComponent, clientId]);

    return {
        mode,
        feeComponentsList,
        listLoading,
        loading,
        actionLoadingId,
        feeComponent,
        validationErrors,
        error,
        isViewingHistory,
        deletedStructures,
        choices,
        handleCreateNew,
        handleViewDetails,
        handleEdit,
        handleDelete,
        handleBackToList,
        handleInputChange,
        handleStructureChange,
        addStructure,
        removeStructure,
        validate,
        handleSave,
        setMode,
        refresh: fetchFeeComponents,
        setDeletedStructures
    };
};
